import React, { useState } from 'react';
import {
  Card,
  Stack,
  Text,
  Group,
  Alert,
  Menu,
  Box,
  ActionIcon,
} from '@mantine/core';
import {
  DiagnosticReport,
  ServiceRequest,
  Patient,
} from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { IconDots, IconEdit } from '@tabler/icons-react';
import cx from 'clsx';
import classes from './ReviewStatusCard.module.css';
import { ReviewStatusData } from '../hooks/useReviewStatus';
import { ReviewerSelectModal } from './ReviewerSelectModal';
import { useReviewActions } from '../hooks/useReviewActions';
import { usePractitioners } from '@/hooks/usePractitioners';

// -----------------------------------------------------------------------------
interface ReviewStatusCardProps {
  diagnosticReport: DiagnosticReport;
  reviewStatusData: ReviewStatusData | null;
  /** When provided, the "Edit reviewers" menu is shown (author only) */
  serviceRequest?: ServiceRequest;
  patient?: Patient;
  onReviewersChanged?: () => void;
  onDiagnosticReportUpdated?: (report: DiagnosticReport) => void;
}

// -----------------------------------------------------------------------------
export const ReviewStatusCard: React.FC<ReviewStatusCardProps> = ({
  diagnosticReport,
  reviewStatusData,
  serviceRequest,
  patient,
  onReviewersChanged,
  onDiagnosticReportUpdated,
}) => {
  const { t } = useTranslation('lims');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const { getPractitionerName } = usePractitioners();

  // Edit is only available when the parent passes the required props (author only)
  const canEdit = !!(serviceRequest && patient && onReviewersChanged);

  const { addReviewers, removePendingReviewer } = useReviewActions({
    diagnosticReport,
    serviceRequest,
    patient,
    onReviewersChanged,
    onDiagnosticReportUpdated,
  });

  const handleSave = async (reviewerIds: string[]) => {
    await addReviewers(reviewerIds);
    setEditModalOpen(false);
  };

  // ---------------------------------------------------------------------------
  if (diagnosticReport.status !== 'preliminary') {
    return null;
  }

  // ---------------------------------------------------------------------------
  return (
    <>
      <Card shadow="sm" padding="md" withBorder>
        <Stack gap="md">
          <Group justify="space-between" align="center">
            <Text size="lg" fw={600}>
              {t('review.status.title')}
            </Text>
            {canEdit && (
              <Menu position="bottom-end" withArrow>
                <Menu.Target>
                  <ActionIcon variant="subtle" color="gray" size="sm">
                    <IconDots size={16} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconEdit size={14} />}
                    onClick={() => setEditModalOpen(true)}
                  >
                    {t('review.status.editReviewers')}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>

          {reviewStatusData ? (
            reviewStatusData.totalTasks > 0 ? (
              <>
                <Group justify="space-between">
                  <Text size="sm">{t('review.status.progress')}</Text>
                  <Text size="sm" fw={600}>
                    {t('review.status.approvedOf', {
                      approved: reviewStatusData.approvedTasks,
                      total: reviewStatusData.totalTasks,
                    })}
                  </Text>
                </Group>

                <Box className={classes.segmentedBar}>
                  {Array.from({ length: reviewStatusData.totalTasks }).map(
                    (_, i) => (
                      <div
                        key={i}
                        className={cx(
                          classes.segment,
                          i < reviewStatusData.approvedTasks
                            ? classes.segmentApproved
                            : classes.segmentPending
                        )}
                      />
                    )
                  )}
                </Box>

                {reviewStatusData.allApproved ? (
                  <Alert color="green" variant="light">
                    <Text size="sm" fw={600}>
                      {t('review.status.allApprovedTitle')}
                    </Text>
                    <Text size="sm">
                      {t('review.status.allApprovedDescription')}
                    </Text>
                  </Alert>
                ) : (
                  <div>
                    <Text size="sm" c="dimmed" mb="xs">
                      {t('review.status.waitingForApproval', {
                        count: reviewStatusData.pendingTasks,
                      })}
                    </Text>
                    {reviewStatusData.approvedReviewerIds.length > 0 && (
                      <Text size="xs" c="green" mb="xs">
                        {t('review.status.approvedBy', {
                          reviewers: reviewStatusData.approvedReviewerIds
                            .map(getPractitionerName)
                            .join(', '),
                        })}
                      </Text>
                    )}
                    {reviewStatusData.pendingReviewerIds.length > 0 && (
                      <Text size="xs" c="dimmed">
                        {t('review.status.pendingFrom', {
                          reviewers: reviewStatusData.pendingReviewerIds
                            .map(getPractitionerName)
                            .join(', '),
                        })}
                      </Text>
                    )}
                  </div>
                )}
              </>
            ) : (
              <Alert color="yellow" variant="light">
                <Text size="sm" fw={600}>
                  {t('review.status.noTasksTitle')}
                </Text>
                <Text size="sm">{t('review.status.noTasksDescription')}</Text>
              </Alert>
            )
          ) : (
            <Alert color="blue" variant="light">
              <Text size="sm">{t('review.status.loading')}</Text>
            </Alert>
          )}
        </Stack>
      </Card>

      {/* ── Edit reviewers modal ─────────────────────────────────────────────── */}
      <ReviewerSelectModal
        opened={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onAddReviewers={handleSave}
        onRemoveReviewer={removePendingReviewer}
      />
    </>
  );
};
